import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Input, Prompt } from 'sonata-react-components';

import { DEFAULT_RADIUS_IN_KM } from '../../../constants';

import './attributionRadiusPrompt.scss';

type Props = {
  onConfirm: (radiusInMeters: number) => void;
  onCancel: () => void;
  defaultRadiusInMeters?: number;
  min?: number;
  max?: number;
  title?: string;
};

type State = {
  radius: number;
};

export function AttributionRadiusPrompt({
  onConfirm,
  onCancel,
  defaultRadiusInMeters,
  min,
  max,
  title
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [{ radius }, setState] = useState<State>({
    radius: defaultRadiusInMeters ?? DEFAULT_RADIUS_IN_KM * 1000
  });

  const MIN_RADIUS = min ?? 50;
  const MAX_RADIUS = max ?? 50000;

  const handleOnChange = (value: string) => {
    const numericValue = Number(value);
    if (!isNaN(numericValue)) {
      setState(prev => ({ ...prev, radius: numericValue }));
    }
  };

  return (
    <Prompt className="attributionRadiusPrompt" closePrompt={onCancel} width="538px">
      <Prompt.Title>
        {title ||
          formatMessage({
            defaultMessage: 'Confirm the radius for your new Activation Areas',
            id: 'poiImporter.prompt.activationAreas.title'
          })}
      </Prompt.Title>
      <div className="attributionRadiusPrompt__input">
        <Input
          className="poiImporterResultList__activationAreasInput"
          description={formatMessage({
            defaultMessage: 'Minimum 50m, maximum 50km',
            id: 'poiImporter.prompt.activationAreas.radiusDescription'
          })}
          id={`activationAreas-input`}
          label={formatMessage({
            defaultMessage: 'Radius',
            id: 'poiImporter.prompt.activationAreas.radiusLabel'
          })}
          name="activationAreas"
          notFloatingPlaceholder
          onChange={value => handleOnChange(value as string)}
          shape="squareUI"
          type="number"
          value={radius}
        />
      </div>
      <Prompt.Buttons position="centered">
        <ButtonUI
          onClick={onCancel}
          shape="squareRounded"
          text={formatMessage({ defaultMessage: 'Cancel', id: 'common.cancel' })}
          type="secondary"
        />
        <ButtonUI
          disabled={!radius || radius < MIN_RADIUS || radius > MAX_RADIUS}
          onClick={() => onConfirm(radius)}
          shape="squareRounded"
          text={formatMessage({
            defaultMessage: 'Confirm & Add',
            id: 'poiImporter.prompt.confirmAdd'
          })}
          type="primary"
        />
      </Prompt.Buttons>
    </Prompt>
  );
}
